// Le moteur plein écran des drills et du chrono (D-098), repris tel quel de Playbook BCL, où il est rodé :
//  * sons synthétisés (Web Audio, aucun fichier), contexte débloqué au premier toucher (Safari iOS) ;
//  * écran maintenu allumé (Wake Lock), redemandé au retour au premier plan ;
//  * parade iOS quand l'écran se verrouille : TOUTES les techniques ensemble (session média, vidéo muette
//    en boucle, audio quasi silencieux en boucle, oscillateur inaudible) — la première qui tient sauve
//    la séance ; le temps des drills est de toute façon calé sur l'horloge et se recale au réveil ;
//  * plein écran dans les deux orientations, sans verrou ;
//  * voix (synthèse du navigateur), conseil d'installer l'app sur l'écran d'accueil (iPhone, onglet Safari).
import { NOSLEEP_MP4, NOSLEEP_WEBM } from './nosleep-media';
import { html, openSheet, $ } from './dom';

// --- Audio -----------------------------------------------------------------------------------------
let ac: AudioContext | null = null;
/** Crée ou réveille le contexte audio ; appelé pendant un geste, il débloque durablement Safari iOS. */
export function unlockAudio(): AudioContext | null {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return null;
    ac ??= new Ctx();
    if (ac.state === 'suspended') void ac.resume();
    const src = ac.createBufferSource();
    src.buffer = ac.createBuffer(1, 1, 22050);
    src.connect(ac.destination); src.start(0);
    return ac;
  } catch { return null; }
}
const ctxAudio = () => { if (!ac) return unlockAudio(); if (ac.state === 'suspended') void ac.resume(); return ac; };

/** Bip court (nouveau signal, départ d'un effort, 3-2-1). */
export function beep(freq = 880, gain = 0.28): void {
  try {
    const a = ctxAudio(); if (!a) return;
    const o = a.createOscillator(), g = a.createGain(), t = a.currentTime;
    o.type = 'square'; o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(gain, t + 0.008); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
    o.connect(g); g.connect(a.destination); o.start(t); o.stop(t + 0.13);
  } catch { /* sans son */ }
}
/** Tic du mode tension (3-2-1). */
export function tick(freq: number): void {
  try {
    const a = ctxAudio(); if (!a) return;
    const o = a.createOscillator(), g = a.createGain(), t = a.currentTime;
    o.type = 'sine'; o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.3, t + 0.008); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
    o.connect(g); g.connect(a.destination); o.start(t); o.stop(t + 0.1);
  } catch { /* sans son */ }
}

// Les sons de fin (réglage du téléphone) : recettes de Playbook, synthétisées.
export type EndSound = 'buzzer' | 'sifflet' | 'cloche' | 'triple';
export const END_SOUNDS: { id: EndSound; name: string; sub: string }[] = [
  { id: 'buzzer', name: 'Buzzer de salle', sub: 'fin de quart-temps' },
  { id: 'sifflet', name: 'Sifflet', sub: 'deux coups' },
  { id: 'cloche', name: 'Cloche', sub: 'douce' },
  { id: 'triple', name: 'Triple bip', sub: 'montant' },
];
function buzzer(a: AudioContext, t0: number) {
  const dur = 0.85, lp = a.createBiquadFilter(), g = a.createGain();
  lp.type = 'lowpass'; lp.frequency.value = 1500; lp.Q.value = 0.7;
  g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(0.45, t0 + 0.02); g.gain.setValueAtTime(0.45, t0 + dur - 0.14); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  lp.connect(g); g.connect(a.destination);
  for (const [f, det] of [[196, 0], [233, 9], [117, -7]] as const) { const o = a.createOscillator(); o.type = 'sawtooth'; o.frequency.value = f; o.detune.value = det; o.connect(lp); o.start(t0); o.stop(t0 + dur); }
}
function cloche(a: AudioContext, t0: number) {
  for (const [mult, amp] of [[1, 0.4], [2.76, 0.22], [5.4, 0.08]] as const) {
    const o = a.createOscillator(), g = a.createGain();
    o.type = 'sine'; o.frequency.value = 660 * mult;
    g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(amp, t0 + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.4);
    o.connect(g); g.connect(a.destination); o.start(t0); o.stop(t0 + 1.5);
  }
}
function triple(a: AudioContext, t0: number) {
  [660, 880, 1175].forEach((f, i) => {
    const dt = i * 0.14, o = a.createOscillator(), g = a.createGain();
    o.type = 'sine'; o.frequency.value = f;
    g.gain.setValueAtTime(0.0001, t0 + dt); g.gain.exponentialRampToValueAtTime(0.4, t0 + dt + 0.012); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.16);
    o.connect(g); g.connect(a.destination); o.start(t0 + dt); o.stop(t0 + dt + 0.18);
  });
}
function sifflet(a: AudioContext, t0: number) {
  for (const [dt, dur] of [[0, 0.22], [0.3, 0.32]] as const) {
    const g = a.createGain();
    g.gain.setValueAtTime(0.0001, t0 + dt); g.gain.exponentialRampToValueAtTime(0.3, t0 + dt + 0.02); g.gain.setValueAtTime(0.3, t0 + dt + dur - 0.05); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + dur);
    const lfo = a.createOscillator(), lfoG = a.createGain();
    lfo.type = 'sine'; lfo.frequency.value = 23; lfoG.gain.value = 0.1; lfo.connect(lfoG); lfoG.connect(g.gain); lfo.start(t0 + dt); lfo.stop(t0 + dt + dur);
    for (const f of [2100, 2108]) { const o = a.createOscillator(); o.type = 'sine'; o.frequency.value = f; o.connect(g); o.start(t0 + dt); o.stop(t0 + dt + dur); }
    g.connect(a.destination);
  }
}
export function playEndSound(id: EndSound): void {
  try {
    const a = ctxAudio(); if (!a) return;
    const t0 = a.currentTime;
    if (id === 'cloche') cloche(a, t0); else if (id === 'triple') triple(a, t0); else if (id === 'sifflet') sifflet(a, t0); else buzzer(a, t0);
  } catch { /* sans son */ }
}

// Réglages du téléphone (pas du compte : chaque appareil a son haut-parleur, son gymnase).
const K = { sound: 'arceau.chrono.son', tension: 'arceau.chrono.tictac', flash: 'arceau.chrono.rouge' };
const get = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const put = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* navigation privée */ } };
export const soundPref = (): EndSound => (END_SOUNDS.some(s => s.id === get(K.sound)) ? get(K.sound) as EndSound : 'buzzer');
export const setSoundPref = (id: EndSound) => put(K.sound, id);
/** Tic-tac 3-2-1 : désactivé par défaut. */
export const tensionPref = () => get(K.tension) === '1';
export const setTensionPref = (on: boolean) => put(K.tension, on ? '1' : '0');
/** Écran rouge à zéro : activé par défaut (l'iPhone ne vibre pas). */
export const flashPref = () => get(K.flash) !== '0';
export const setFlashPref = (on: boolean) => put(K.flash, on ? '1' : '0');

/** Fin d'un décompte : le son choisi, la vibration (Android), l'écran rouge (tous, iPhone compris). */
export function alarm(): void {
  playEndSound(soundPref());
  try { navigator.vibrate?.([300, 120, 300, 120, 400]); } catch { /* pas de vibreur */ }
  if (flashPref()) flash();
}
/** Écran rouge plein écran, qui ne bloque jamais rien (pointer-events: none), retiré tout seul. */
export function flash(): void {
  let f = document.getElementById('dr-flash');
  if (!f) { f = document.createElement('div'); f.id = 'dr-flash'; f.setAttribute('aria-hidden', 'true'); document.body.append(f); }
  f.classList.remove('on'); void f.offsetWidth; f.classList.add('on');
  window.setTimeout(() => document.getElementById('dr-flash')?.remove(), 1700);
}

// --- Voix ------------------------------------------------------------------------------------------
export function speak(text: string): void {
  try {
    if (!('speechSynthesis' in window) || !text) return;
    const u = new SpeechSynthesisUtterance(text);
    const v = speechSynthesis.getVoices().find(x => x.lang.toLowerCase().startsWith('fr'));
    if (v) { u.voice = v; u.lang = v.lang; } else u.lang = 'fr-FR';
    speechSynthesis.cancel();   // pas d'empilement si les annonces se suivent de près
    speechSynthesis.speak(u);
  } catch { /* sans voix */ }
}
export function hush(): void { try { speechSynthesis.cancel(); } catch { /* sans voix */ } }

// --- Écran allumé, parade iOS, plein écran ----------------------------------------------------------------
type Sentinel = { release(): Promise<void>; addEventListener?(t: string, f: () => void): void };
let lock: Sentinel | null = null;
let holders = 0;
let audioEl: HTMLAudioElement | null = null, audioUrl: string | null = null, video: HTMLVideoElement | null = null;
let osc: { o: OscillatorNode; g: GainNode } | null = null;

async function wakeLock(): Promise<void> {
  try {
    const wl = (navigator as unknown as { wakeLock?: { request(t: 'screen'): Promise<Sentinel> } }).wakeLock;
    if (!wl || lock) return;
    lock = await wl.request('screen');
    lock.addEventListener?.('release', () => { lock = null; });
  } catch { lock = null; }
}
/** Un vrai WAV de quelques secondes, à ±1 près (le silence parfait, iOS l'ignore). */
function silentWav(seconds = 10): string {
  const rate = 8000, n = rate * seconds, size = n * 2;
  const buf = new ArrayBuffer(44 + size), v = new DataView(buf);
  const w = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, 'RIFF'); v.setUint32(4, 36 + size, true); w(8, 'WAVE'); w(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); w(36, 'data'); v.setUint32(40, size, true);
  for (let i = 0; i < n; i++) v.setInt16(44 + i * 2, i % 2 ? 1 : -1, true);
  return URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
}
function mediaSession(title: string | null): void {
  try {
    const ms = navigator.mediaSession;
    if (!ms) return;
    const acts: MediaSessionAction[] = ['play', 'pause', 'stop', 'previoustrack', 'nexttrack'];
    if (title) {
      if (typeof MediaMetadata !== 'undefined') ms.metadata = new MediaMetadata({ title, artist: 'Arceau', album: 'Séance' });
      ms.playbackState = 'playing';
      // Un toucher sur les commandes de l'écran verrouillé ne doit pas tuer la session.
      for (const a of acts) { try { ms.setActionHandler(a, () => undefined); } catch { /* action absente */ } }
    } else {
      ms.playbackState = 'none'; ms.metadata = null;
      for (const a of acts) { try { ms.setActionHandler(a, null); } catch { /* action absente */ } }
    }
  } catch { /* pas de session média */ }
}
function keepAliveStart(title: string): void {
  mediaSession(title);
  try {
    if (!video) {
      video = document.createElement('video');
      video.setAttribute('playsinline', ''); video.setAttribute('webkit-playsinline', ''); video.muted = true; video.loop = true;
      video.className = 'dr-nosleep';
      for (const [src, type] of [[NOSLEEP_WEBM, 'video/webm'], [NOSLEEP_MP4, 'video/mp4']]) { const s = document.createElement('source'); s.src = src!; s.type = type!; video.append(s); }
      document.body.append(video);
      void video.play().catch(() => undefined);
    }
  } catch { video = null; }
  try {
    if (!audioEl) {
      audioUrl = silentWav(10);
      audioEl = new Audio(audioUrl);
      audioEl.loop = true; audioEl.volume = 0.02;   // très bas mais pas nul (0 : iOS peut l'ignorer)
      audioEl.setAttribute('playsinline', '');
      void audioEl.play().catch(() => undefined);
    }
  } catch { audioEl = null; }
  try {
    const a = ctxAudio();
    if (a && !osc) { const o = a.createOscillator(), g = a.createGain(); o.frequency.value = 30; g.gain.value = 0.0005; o.connect(g); g.connect(a.destination); o.start(); osc = { o, g }; }
  } catch { osc = null; }
}
function keepAliveStop(): void {
  mediaSession(null);
  try { video?.pause(); video?.remove(); } catch { /* déjà retirée */ }
  video = null;
  try { audioEl?.pause(); } catch { /* déjà arrêté */ }
  audioEl = null;
  if (audioUrl) { URL.revokeObjectURL(audioUrl); audioUrl = null; }
  try { osc?.o.stop(); osc?.o.disconnect(); osc?.g.disconnect(); } catch { /* déjà arrêté */ }
  osc = null;
}
// Retour au premier plan : le navigateur a relâché le Wake Lock et a pu mettre l'audio en pause.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible' || !holders) return;
  void wakeLock();
  if (audioEl?.paused) void audioEl.play().catch(() => undefined);
  if (video?.paused) void video.play().catch(() => undefined);
});

/** Écran allumé (chrono) : sans la parade iOS ni le plein écran. */
export function holdScreen(): () => void {
  holders++;
  void wakeLock();
  let done = false;
  return () => {
    if (done) return; done = true;
    holders = Math.max(0, holders - 1);
    if (!holders) { void lock?.release().catch(() => undefined); lock = null; }
  };
}
/** Un drill démarre : écran allumé, parade iOS, plein écran (les deux orientations). Rend de quoi tout arrêter. */
export function engage(host: HTMLElement, title: string): () => void {
  unlockAudio();
  const release = holdScreen();
  keepAliveStart(title);
  try { void host.requestFullscreen?.().catch(() => undefined); } catch { /* pas de plein écran (iPhone) */ }
  let done = false;
  return () => {
    if (done) return; done = true;
    keepAliveStop();
    hush();
    release();
    try { if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined); } catch { /* déjà sorti */ }
  };
}

// --- iPhone, onglet Safari : prévenir une fois ---------------------------------------------------------------
const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const standalone = () => { try { return matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true; } catch { return false; } };
let warned = false;
/** Dans un onglet Safari, l'iPhone suspend tout écran éteint : on le dit une fois, sans bloquer. Vrai : lancer tout de suite. */
export function readyToRun(go: () => void): boolean {
  if (warned || !isIos() || standalone()) return true;
  warned = true;
  openSheet('Installe l’app pour les séances', html`
    <p>Dans un onglet Safari, quand l’écran s’éteint, l’iPhone suspend la page : le décompte se fige et la voix s’arrête.</p>
    <div class="dr-note"><span aria-hidden="true">🛈</span><span>Safari › <b>Partager</b> › <b>« Sur l’écran d’accueil »</b>, puis ouvre l’app depuis son icône : la séance tient bien mieux écran éteint.</span></div>
    <div class="btn-row"><button type="button" class="btn btn-ghost" data-close>Plus tard</button><button type="button" class="btn btn-primary" id="dr-ios-go">Lancer quand même</button></div>`,
  (sheet, close) => { $(sheet, '#dr-ios-go')!.addEventListener('click', () => { close(); go(); }); });
  return false;
}
